import { useCallback, useEffect, useState } from "react"

// Tracks whether a horizontally-scrollable element can scroll further left/right
// and exposes a smooth `scroll` helper. Used to drive the chevron affordances on
// the magic buttons row and the prompt snippet row.
export function useScrollNavigation(scrollRef: React.RefObject<HTMLDivElement | null>) {
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const { scrollLeft, scrollWidth, clientWidth } = el
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
  }, [scrollRef])

  useEffect(() => {
    updateScrollState()
    const el = scrollRef.current
    if (!el) return

    el.addEventListener("scroll", updateScrollState)
    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(el)
    for (const child of el.children) resizeObserver.observe(child)

    return () => {
      el.removeEventListener("scroll", updateScrollState)
      resizeObserver.disconnect()
    }
  }, [updateScrollState, scrollRef])

  const scroll = useCallback(
    (direction: "left" | "right") => {
      const el = scrollRef.current
      if (!el) return

      const scrollAmount = el.clientWidth * 0.8
      el.scrollTo({
        left: el.scrollLeft + (direction === "left" ? -scrollAmount : scrollAmount),
        behavior: "smooth",
      })
    },
    [scrollRef]
  )

  return { canScrollLeft, canScrollRight, scroll }
}
